import { Component, OnInit, OnDestroy, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../../services/auth/auth.service';
import { OrganizationService } from '../../../services/organizations/organization.service';
import { UnauthorizedPageComponent } from '../../pages/unauthorized-page/unauthorized-page.component';
import { ForbiddenPageComponent } from '../../pages/forbidden-page/forbidden-page.component';
import { Subject, takeUntil } from 'rxjs';

type ProtectionState = 'content' | 'unauthorized' | 'forbidden' | 'pending-organization';

@Component({
  selector: 'app-protected-content',
  standalone: true,
  imports: [CommonModule, UnauthorizedPageComponent, ForbiddenPageComponent],
  templateUrl: './protected-content.component.html',
  host: {
    style: 'display: block; width: 100%;'
  }
})
export class ProtectedContentComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private organizationService = inject(OrganizationService);
  private destroy$ = new Subject<void>();

  /** Required roles to access content. If empty, only authentication is required. */
  requiredRoles = input<string[]>([]);

  /** If true, users of non-approved (PENDING/REJECTED) organizations are blocked. */
  requireApprovedOrganization = input<boolean>(false);

  protectionState: ProtectionState = 'content';

  ngOnInit(): void {
    this.checkAccess();

    // Re-check access when user roles change
    this.authService.userRoles$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.checkAccess());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private checkAccess(): void {
    const isLoggedIn = this.authService.isLoggedIn$.value;

    if (!isLoggedIn) {
      this.protectionState = 'unauthorized';
      return;
    }

    // Check if required roles are specified
    const requiredRoles = this.requiredRoles();
    if (requiredRoles.length > 0) {
      const userRoles = this.authService.userRoles$.value;
      const hasRequiredRole = requiredRoles.some(role => userRoles.includes(role));

      if (!hasRequiredRole) {
        this.protectionState = 'forbidden';
        return;
      }
    }

    // Check organization admission state if required
    if (this.requireApprovedOrganization()) {
      this.organizationService.isOrganizationPending$()
        .pipe(takeUntil(this.destroy$))
        .subscribe(isPending => {
          this.protectionState = isPending ? 'pending-organization' : 'content';
        });
      return;
    }

    // User has required access
    this.protectionState = 'content';
  }
}
